import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { AppStore } from '@app/app.store';
import { ParameterResolutionService } from '@app/core/services/parameter/parameter.service';
import { DivisionService } from '@app/features/division/services/division.service';
import { Page } from '@app/shared/models/page.interface';
import { SearchParams } from '@app/shared/models/search-params.model';
import { User } from '@app/shared/models/user.interface';
import { DataTablePageState } from '@components/data/data-table-new/directives/datatable-pagination.directive';
import { DataTableSortState } from '@components/data/data-table-new/directives/datatable-sort.directive';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface UserActiveResponse {
  [id: string]: string;
}

const WORKDAY_PARAMETER = 'USR_DEFAULT_WORKDAY';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(
    protected http: HttpClient,
    protected appStore: AppStore,
    protected divisionService: DivisionService,
    protected appConfigService: AppConfigService,
    protected parameterResolutionService: ParameterResolutionService
  ) {}

  getNew(): Observable<User> {
    const defaultWorkday = this.parameterResolutionService.resolveNumericValue(WORKDAY_PARAMETER);
    return this.divisionService.getAll().pipe(
      map(divisions => {
        let user = {
          workday: defaultWorkday,
          timezone: this.appStore.state.session.user.timezone,
          userDivisions: []
        } as User;
        if (divisions.length === 1) {
          user.userDivisions.push({ id: null, user: null, division: divisions[0] });
        }
        return user;
      })
    );
  }

  /**
   * Get User by id
   * @param id User id
   */
  getById(id: string): Observable<User> {
    return this.http.get<User>(`${this.appConfigService.getApi()}/users/${id}`);
  }

  getByIds(ids: string[]): Observable<User[]> {
    return this.http.post<User[]>(`${this.appConfigService.getApi()}/users/ids`, { ids });
  }

  /**
   * Get User by username
   * @param username User username
   */
  getByUsername(username: string): Observable<User> {
    return this.http.get<User>(`${this.appConfigService.getApi()}/public/user/${username}`);
  }

  /**
   * Get all Users
   */
  getAll(): Observable<User[]> {
    return this.http.get<User[]>(`${this.appConfigService.getApi()}/users/all`);
  }

  search(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string
  ): Observable<Page<User>> {
    const params: SearchParams = {
      search,
      filter,
      pageNumber: pageState.index,
      pageSize: pageState.size,
      sortDirection: sortState.direction.toString().toUpperCase(),
      sortField: sortState.column
    };
    return this.http.post<Page<User>>(`${this.appConfigService.getApi()}/users/search`, params);
  }

  getByProfile(profileId: string, page: DataTablePageState, sort: DataTableSortState): Observable<Page<User>> {
    let params = new HttpParams();
    params = params.append('page', page.index.toString());
    params = params.append('size', page.size.toString());
    params = params.append('sort', sort.column + ',' + sort.direction);

    return this.http.get<Page<User>>(`${this.appConfigService.getApi()}/users/all/profile/${profileId}`, {
      params
    });
  }

  save(user: User): Observable<User> {
    return user.id != null ? this.update(user.id, user) : this.create(user);
  }

  create(user: User): Observable<User> {
    return this.http.post<User>(`${this.appConfigService.getApi()}/users`, user);
  }

  update(id: string, user: User): Observable<User> {
    return this.http.put<User>(`${this.appConfigService.getApi()}/users/${id}`, user);
  }

  closeSession(ids: string[]): Observable<any> {
    return this.http.put(`${this.appConfigService.getApi()}/auth/users`, { elements: ids });
  }

  changeState(selection: string[], active: boolean): Observable<UserActiveResponse> {
    return this.http.post<UserActiveResponse>(`${this.appConfigService.getApi()}/users/active/`, {
      ids: selection,
      active
    });
  }

  changeProfile(selection: string[], newProfile: string): Observable<any> {
    return this.http.post<any>(`${this.appConfigService.getApi()}/users/profile/`, {
      ids: selection,
      profileId: newProfile
    });
  }

  changeImage(userId: string, imageId: string): Observable<any> {
    return this.http.patch(`${this.appConfigService.getApi()}/users/${userId}/image/${imageId}`, {});
  }
}
